/**
 * Multi-window ("Window Volume") discount tiers — DISPLAY COPY ONLY.
 *
 * Checkout prices from the database row vendor_discounts.discount_id for
 * "Window Volume" (volume_tiers JSON, enabled 2026-10-25, owner-approved).
 * These constants only drive shopper-facing messages (cart nudge, banners,
 * landing copy). If the DB tiers change, change these to match.
 *
 * Counted on the number of blinds/shades (quantity) in the cart that are not
 * already at a sale price.
 */
export const WINDOW_VOLUME_TIERS = [
  { min: 3, max: 5, percent: 3 },
  { min: 6, max: 10, percent: 7 },
  { min: 11, max: null as number | null, percent: 12 },
] as const;

export type VolumeTier = (typeof WINDOW_VOLUME_TIERS)[number];

/** Tier that applies to `qty` windows, or null below the first tier. */
export function volumeTierFor(qty: number): VolumeTier | null {
  return WINDOW_VOLUME_TIERS.find((t) => qty >= t.min && (t.max === null || qty <= t.max)) || null;
}

/** Next better tier and how many more windows reach it, or null at the top tier. */
export function nextVolumeTier(qty: number): { tier: VolumeTier; more: number } | null {
  const next = WINDOW_VOLUME_TIERS.find((t) => t.min > qty);
  return next ? { tier: next, more: next.min - qty } : null;
}

/** "3% off 3–5 windows, 7% off 6–10, 12% off 11+" */
export const VOLUME_TIERS_SUMMARY = 'Save 3% on 3–5 windows, 7% on 6–10, 12% on 11+';

/**
 * One promotion per order (owner decision 2026-10-25). Blinds priced to match
 * the matched retailer's sale price already carry their promotion: they neither count
 * toward nor get the multi-window discount, and a promo code can't be added to
 * a cart that has them. A code and the multi-window discount never stack
 * either (checkout keeps the larger). Enforced in CommerceHandler.priceCart.
 */
export const VOLUME_TIERS_TERMS =
  "One promotion per order: items already at a sale price and promo codes don't combine with it";
